import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';
import SlashList from '../components/SlashList';

// phones_extra / emails_extra are stored as a JSON-encoded array; parse
// defensively so a fresh install or empty string just means "none set".
function parseList(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export default function CompanyInfo() {
  const { t, language } = useLanguage();
  const [settings, setSettings] = useState({});
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
    API.get('/brands').then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const phones = [settings.phone, ...parseList(settings.phones_extra)].filter(Boolean);
  const emails = [settings.email, ...parseList(settings.emails_extra)].filter(Boolean);

  return (
    <PageLayout>
      <SEO title={t('companyInfo.title')} description={settings.about_content ? settings.about_content.slice(0, 160) : undefined} />
      <PageBanner en="COMPANY PROFILE" title={t('companyInfo.title')} breadcrumbs={[{ label: t('companyInfo.title') }]} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <p className="text-primary font-btn text-xs font-semibold tracking-[0.3em] uppercase mb-2">{t('companyInfo.eyebrow')}</p>
              <h2 className="section-title mb-6">{(language === 'ja' && settings.about_title_ja) || settings.about_title || 'Sankyo Machine Tools'}</h2>
              <div className="text-gray-600 leading-relaxed space-y-4">
                {((language === 'ja' && settings.about_content_ja) || settings.about_content || 'Sankyo Machine Tools Philippines Inc. is a Japanese Trading company based in Philippines.').split('\n\n').map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            {/* Mission */}
            <div className="border-l-4 border-accent pl-6 py-2 bg-accent/5">
              <h3 className="font-bold text-ink mb-2">
                {(language === 'ja' && settings.mission_title_ja) || settings.mission_title || t('companyInfo.missionTitle')}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {(language === 'ja' && settings.mission_content_ja) || settings.mission_content || t('companyInfo.missionText')}
              </p>
            </div>

            {/* Brands */}
            {brands.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-primary">{t('companyInfo.brandsTitle')}</h3>
                  <Link to="/brands" className="hidden sm:flex items-center gap-1.5 text-xs text-primary hover:text-accent transition-colors font-medium">
                    {t('common.viewAll')} <ArrowRightIcon className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {brands.map(b => (
                    <Link key={b.id} to="/brands"
                      className="bg-primary/5 border border-primary/20 text-primary text-xs px-3 py-1.5 font-medium hover:bg-primary hover:text-white hover:border-primary transition-colors">
                      {b.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company details card */}
            <div className="bg-charcoal text-white p-6">
              <h3 className="text-lg font-bold mb-5 pb-3 border-b border-white/20">{t('companyInfo.detailsTitle')}</h3>
              <ul className="space-y-4 text-sm">
                {[
                  [t('companyInfo.established'), (language === 'ja' && settings.company_established_ja) || settings.company_established || '2016 (Philippines)'],
                  [t('companyInfo.parentCompany'), (language === 'ja' && settings.company_parent_ja) || settings.company_parent || 'Sankyo Seiki Co., Ltd.'],
                  [t('companyInfo.headquarters'), (language === 'ja' && settings.company_headquarters_ja) || settings.company_headquarters || 'Kyoto, Japan'],
                  [t('companyInfo.specialization'), (language === 'ja' && settings.company_specialization_ja) || settings.company_specialization || t('companyInfo.specializationValue')],
                  [t('companyInfo.region'), (language === 'ja' && settings.company_region_ja) || settings.company_region || t('companyInfo.regionValue')],
                ].map(([k, v]) => (
                  <li key={k}>
                    <span className="text-primary text-xs uppercase tracking-wider block mb-0.5 font-btn font-semibold">{k}</span>
                    <span className="text-gray-200">{v}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact card */}
            <div className="bg-gray-50 border border-gray-200 p-6">
              <h3 className="font-bold text-ink mb-4">{t('companyInfo.contactInfo')}</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li>
                  <span className="font-medium text-primary block mb-0.5">{t('contact.phone')}</span>
                  <SlashList values={phones.length ? phones : ['+603-2720-9719']} hrefPrefix="tel:" linkClassName="hover:text-primary transition-colors" />
                </li>
                <li>
                  <span className="font-medium text-primary block mb-0.5">{t('contact.email')}</span>
                  <SlashList values={emails.length ? emails : ['example@sankyomt.ph']} hrefPrefix="mailto:" linkClassName="hover:text-primary transition-colors" />
                </li>
                <li>
                  <span className="font-medium text-primary block mb-0.5">{t('contact.address')}</span>
                  <span className="block break-words">{settings.address}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
